import { useState, useEffect, useRef } from "react";
import { FileX } from "lucide-react";
import {
  SandpackProvider,
  SandpackLayout,
  SandpackPreview,
} from "@codesandbox/sandpack-react";
import { CLAUDE_SERVER_BASE_URL, CREWAI_AGENTS_URL } from "../constants/apiUrl";
import axios from "axios";
import AppLoader from "../components/Loader";
import { base64UrlToUuid } from "../helpers/shared";
import { ACCESS_TOKEN } from "../constants/constants";
import { getCookie, getTenantIDFromToken } from "../helpers/jwt";

function WebsitePreview() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [accessToken, setAccessToken] = useState(getCookie(ACCESS_TOKEN));
  const [tenantId, setTenantId] = useState(getTenantIDFromToken(accessToken));
  const didGenerationRun = useRef(false);
  const editorRef = useRef(null);

  const agentId = base64UrlToUuid(
    new URLSearchParams(window.location.search).get("agentId")
  );
  const agentName = new URLSearchParams(window.location.search).get(
    "agentName"
  );

  const fetchAgentTemplate = async (agentId, tenantId) => {
    const payload = {
      agentId,
      tenantId,
    };

    try {
      const response = await axios.post(
        `${CLAUDE_SERVER_BASE_URL}/api/agent`,
        payload
      );
      if (response.error) {
        console.error("Error fetching agent template:", response.error);
        return null;
      }
      const data = response.data;
      // console.log("Template data", data);
      if (data.success && data.template) return data.template;
      return null;
    } catch (err) {
      console.error("Error fetching agent template:", err);
      return null;
    }
  };

  const transformFilesForSandpack = (filesArray) => {
    const obj = {};
    filesArray.forEach((file) => {
      obj[`/${file.filename}`] = { code: file.content };
    });
    return obj;
  };

  const showAgentTemplate = async () => {
    setLoading(true);
    try {
      const existingTemplate = await fetchAgentTemplate(agentId, tenantId);

      if (existingTemplate) {
        const lastRunOutput = await getLatestRunOutput(agentId);
        console.log("lastRunOutput", lastRunOutput);
        const updatedTemplate = addRunDataToTemplate(
          lastRunOutput,
          existingTemplate
        );
        setFiles(transformFilesForSandpack(updatedTemplate));
        console.log("updatedTemplate", updatedTemplate);
        return;
      }
    } catch (err) {
      console.error("Error occurred while fetching template:", err);
    } finally {
      setLoading(false);
    }
  };

  const getLatestRunOutput = async (agentId) => {
    try {
      const result = await axios.get(
        `${CREWAI_AGENTS_URL}/agent-runs?agent_id=${agentId}&status=completed&has_structured_response=true`
      );

      if (result && !result.error) {
        console.log("result.data", result.data);
        const data = result.data;
        if (data && data.length > 0) {
          const lastItem = data[0];
          const run_output = lastItem?.run_output;
          const rawResponse = run_output?.response?.pydantic;
          const responseJson = rawResponse;
          return responseJson;
        } else {
          return {};
        }
      }

      return null;
    } catch (error) {
      console.error("Error fetching agent template:", error);
      return null;
    }
  };

  const addRunDataToTemplate = (lastRunOutput, websiteTemplate) => {
    const updatedTemplate = websiteTemplate.map((file) => {
      if (file.filename === "data.js") {
        file.content = `const userDataJson = ${JSON.stringify(lastRunOutput)};`;
        return file;
      } else {
        return file;
      }
    });
    console.log("updatedTemplate", updatedTemplate);
    return updatedTemplate;
  };

  useEffect(() => {
    console.log("Inside Homepage useEffect");
    axios.defaults.headers.common["Authorization"] = `Bearer ${accessToken}`;
    if (!agentId) {
      setFiles([]);
      return;
    }
    if (didGenerationRun.current) return;
    didGenerationRun.current = true;
    showAgentTemplate();
  }, [accessToken]);

  if (loading)
    return (
      <div className="h-screen">
        <AppLoader />
      </div>
    );

  if (!files || Object.keys(files).length === 0) {
    return (
      <div className="flex h-screen items-center justify-center p-6">
        <div className="flex w-full max-w-sm flex-col items-center rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100">
            <FileX className="h-6 w-6 text-gray-500" />
          </div>

          <h2 className="mb-1 text-base font-semibold text-gray-700">
            No App Available
          </h2>
          <p className="text-sm text-gray-500">
            Create an app to see a preview here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <SandpackProvider
      files={files}
      theme="light"
      template="static"
      style={{ height: "100vh" }}
      customSetup={{ entry: "/index.html" }}
      options={{
        externalResources: [
          "https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css",
          "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css",
          "https://cdn.datatables.net/1.13.7/css/jquery.dataTables.min.css",
        ],
      }}
    >
      <SandpackLayout className="h-full">
        <div className="h-full w-full">
          <SandpackPreview
            className="h-full"
            showOpenInCodeSandbox={false}
            showRefreshButton={false}
          />
        </div>
      </SandpackLayout>
    </SandpackProvider>
  );
}
export default WebsitePreview;
